import React, { useState, useEffect } from 'react';
import { Task, TaskLocationType, TaskStage } from '../../../../types/Task';
import { Effort } from '../../../../types/Effort';
import GoogleGPSMap from '../../../../components/GoogleGPSMap';
import { taskService } from '../../../../services/quantifiedTaskService';
import { useAuth } from '../../../../contexts/AuthContext';
import { usePermissions } from '../../../../hooks/usePermissions';
import ResponsiveModal from '../ResponsiveModal';
import {
  calculateActualTotalValue,
  calculateExpectedTotalValueWithLogic,
  getEarliestEffortDate,
  getEffortCount,
  getLatestEffortDate,
} from '../../utils';
import DistanceDisplay from '../../../../components/DistanceDisplay';
import { Button, ButtonGroup, Form, InputGroup, Alert, Row, Col } from '@/components/ui';
import { describeActionError } from '../../../../utils/loadError';

const COMMENTS_FIELD_ID = 'completion-comments';

interface CompletionModalProps {
  visible: boolean;
  onClose: () => void;
  task: Task;
  onComplete: () => void;
}

// getLatestEffortDate used to be defined here, scanning task.efforts. It now comes from ../../utils
// so it works from the server's effortSummary too: this modal is always opened with a row from the
// task list, and list rows no longer carry effort arrays.

// Helper function to get Expected Total Value using consistent calculation logic
const getExpectedTotalValue = (task: Task): number => {
  
  // Log detailed costing information
  if (task.taskCostings && task.taskCostings.length > 0) {
    const costing = task.taskCostings[0];
  }
  
  const result = calculateExpectedTotalValueWithLogic(task, { enableDebug: true });
  return result;
};



const CompletionModal: React.FC<CompletionModalProps> = ({
  visible,
  onClose,
  task,
  onComplete,
}) => {
  // Get planned values from task data
  const plannedValues = {
    accomplishment: task.quantifiedTasks?.[0]?.identifiedQuantity || 0,
    unitOfMeasurement: task.quantifiedTasks?.[0]?.unitOfMeasure || '',
    startLatitude: task.taskLocations?.find(loc => loc.type === 'START')?.latitude || 0,
    startLongitude: task.taskLocations?.find(loc => loc.type === 'START')?.longitude || 0,
    endLatitude: task.taskLocations?.find(loc => loc.type === 'END')?.latitude || 0,
    endLongitude: task.taskLocations?.find(loc => loc.type === 'END')?.longitude || 0,
  };

  // Add console log to debug task locations

  const [completedValues, setCompletedValues] = useState({
    accomplishment: plannedValues.accomplishment,
    unitOfMeasurement: plannedValues.unitOfMeasurement,
    startLatitude: plannedValues.startLatitude,
    startLongitude: plannedValues.startLongitude,
    endLatitude: plannedValues.endLatitude,
    endLongitude: plannedValues.endLongitude,
  });

  const [completionComments, setCompletionComments] = useState('');
  const [commentsError, setCommentsError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  
  // Get the default completion date: task completion date, last effort date, or current date
  const getDefaultCompletionDate = (): string => {
    // First priority: existing completion date
    if (task.completionDate) {
      const date = new Date(task.completionDate).toISOString().split('T')[0];
      return date;
    }
    
    // Second priority: last effort date
    const latestEffortDate = getLatestEffortDate(task);
    if (latestEffortDate) {
      const date = latestEffortDate.toISOString().split('T')[0];
      return date;
    }
    
    // Fallback: current date
    const date = new Date().toISOString().split('T')[0];
    return date;
  };

  // Helper function to get the date source description
  const getDateSourceDescription = (): string => {
    if (task.completionDate) {
      return 'Using existing completion date';
    }
    
    const latestEffortDate = getLatestEffortDate(task);
    if (latestEffortDate) {
      const effortCount = getEffortCount(task);
      const latestDateStr = latestEffortDate.toISOString().split('T')[0];
      return `Using latest effort date: ${latestDateStr} (${effortCount} effort${effortCount !== 1 ? 's' : ''} found)`;
    }
    
    return 'Using current date (no efforts found)';
  };

  // Add state for completion date
  const [completionDate, setCompletionDate] = useState<string>(getDefaultCompletionDate());
  // Add state for GPS change checkbox
  const [gpsChanged, setGpsChanged] = useState(false);
  // Add state to track if quantity has changed
  const [quantityChanged, setQuantityChanged] = useState(false);

  const [validationError, setValidationError] = useState<string | null>(null);

  const [isMapVisible, setIsMapVisible] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [activeLocationType, setActiveLocationType] = useState<'start' | 'end'>('start');

  const { currentUser } = useAuth();
  const { hasPermission } = usePermissions();

  // Reset completed values when task changes
  useEffect(() => {
    setCompletedValues({
      accomplishment: plannedValues.accomplishment,
      unitOfMeasurement: plannedValues.unitOfMeasurement,
      startLatitude: plannedValues.startLatitude,
      startLongitude: plannedValues.startLongitude,
      endLatitude: plannedValues.endLatitude,
      endLongitude: plannedValues.endLongitude,
    });
    setCompletionDate(getDefaultCompletionDate());
    setGpsChanged(false);
    setQuantityChanged(false);
  }, [task]);

  // Check if quantity has changed
  useEffect(() => {
    setQuantityChanged(completedValues.accomplishment !== plannedValues.accomplishment);
    
    // If quantity is reset to original value, reset GPS changed checkbox
    if (completedValues.accomplishment === plannedValues.accomplishment) {
      setGpsChanged(false);
    }
  }, [completedValues.accomplishment, plannedValues.accomplishment]);

  const isValidSubmission = (): boolean => {
    // Check for valid accomplishment value - allow 0 for completion if user has permission
    if (completedValues.accomplishment < 0) {
      setValidationError('Completion accomplishment cannot be negative');
      return false;
    }
    
    // Check if user can submit 0 accomplishment
    if (completedValues.accomplishment === 0 && !hasPermission('quantified:zero_accomplishment')) {
      setValidationError('You do not have permission to submit a 0 completion accomplishment. Please contact an administrator.');
      return false;
    }

    // Check if completion date is not earlier than the actual start date
    // Use earliest effort date if available, otherwise fall back to planned start date (identifiedDate)
    const earliestEffortDate = getEarliestEffortDate(task);
    const actualStartDate = earliestEffortDate || (task.identifiedDate ? new Date(task.identifiedDate) : null);
    
    if (actualStartDate) {
      // Normalize dates to compare only the date portion (ignore time and timezone)
      // Extract year, month, day components and create dates in local timezone for consistent comparison
      const normalizeDate = (date: Date | string): Date => {
        let year: number, month: number, day: number;
        
        // If it's a date string in YYYY-MM-DD format, parse it directly
        if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
          [year, month, day] = date.split('-').map(Number);
          // month is 1-indexed in the string, but Date constructor expects 0-indexed
          return new Date(year, month - 1, day);
        }
        
        // For Date objects, extract UTC date components to avoid timezone shifts
        // This ensures we get the actual calendar date regardless of timezone
        const d = date instanceof Date ? date : new Date(date);
        year = d.getUTCFullYear();
        month = d.getUTCMonth();
        day = d.getUTCDate();
        
        // Create a new date in local timezone using the UTC date components
        // This ensures consistent comparison: same calendar date = same normalized date
        return new Date(year, month, day);
      };
      
      const normalizedStartDate = normalizeDate(actualStartDate);
      const selectedCompletionDate = normalizeDate(completionDate);
      
      if (selectedCompletionDate < normalizedStartDate) {
        setValidationError('Completion date cannot be earlier than the Start Date');
        return false;
      }
    }

    // If accomplishments are different and GPS changed is checked, at least one coordinate must be different
    if (quantityChanged && gpsChanged) {
      const hasCoordinateChanges = 
        completedValues.startLatitude !== plannedValues.startLatitude ||
        completedValues.startLongitude !== plannedValues.startLongitude ||
        completedValues.endLatitude !== plannedValues.endLatitude ||
        completedValues.endLongitude !== plannedValues.endLongitude;

      if (!hasCoordinateChanges) {
        setValidationError(
          'You indicated that GPS locations changed, but no coordinates have been modified. ' +
          'Please update at least one coordinate or uncheck the GPS changed box.'
        );
        return false;
      }
    }

    setValidationError(null);
    return true;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name === 'completionComments') {
      setCompletionComments(value);
      if (commentsError) setCommentsError(null);
    } else if (name === 'completionDate') {
      setCompletionDate(value);
    } else if (name === 'gpsChanged') {
      setGpsChanged((e.target as HTMLInputElement).checked);
    } else {
      setCompletedValues(prev => ({
        ...prev,
        [name]: name.includes('Latitude') || name.includes('Longitude') || name === 'accomplishment' 
          ? Number(value) 
          : value,
      }));
    }
  };

  const handleLocationSelect = (lat: number, lng: number, type: 'start' | 'end') => {
    // Only allow location changes if GPS changed is checked OR if map is visible
    if (!gpsChanged && !showMap) return;
    
    setCompletedValues(prev => ({
      ...prev,
      ...(type === 'start' 
        ? { startLatitude: lat, startLongitude: lng }
        : { endLatitude: lat, endLongitude: lng }
      )
    }));
    
    // If user is changing GPS via map, automatically set gpsChanged to true
    if (showMap && !gpsChanged) {
      setGpsChanged(true);
    }
  };

  useEffect(() => {
    isValidSubmission();
  }, [completedValues, gpsChanged, completionDate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isValidSubmission()) {
      // Check if task has no efforts and require comments
      const hasNoEfforts = getEffortCount(task) === 0;
      if (hasNoEfforts && !completionComments.trim()) {
        setCommentsError('Comments required when no efforts are recorded');
        setTimeout(() => document.getElementById(COMMENTS_FIELD_ID)?.focus(), 0);
        return;
      }
      
      try {
        setSubmitError(null);
        // If GPS didn't change, use the original coordinates
        const locationData = gpsChanged ? [
          {
            type: 'START',
            latitude: completedValues.startLatitude,
            longitude: completedValues.startLongitude
          },
          {
            type: 'END',
            latitude: completedValues.endLatitude,
            longitude: completedValues.endLongitude
          }
        ] : [
          {
            type: 'START',
            latitude: plannedValues.startLatitude,
            longitude: plannedValues.startLongitude
          },
          {
            type: 'END',
            latitude: plannedValues.endLatitude,
            longitude: plannedValues.endLongitude
          }
        ];
        
        // Add debugging logs
        
        // Ensure the completion date is properly formatted as an ISO string
        // This creates a date at noon UTC to avoid timezone issues
        const formattedCompletionDate = completionDate 
          ? new Date(`${completionDate}T12:00:00Z`).toISOString() 
          : undefined;

          
        
        // Calculate actual total value using consistent calculation logic
        
        // Log detailed costing information
        if (task.taskCostings && task.taskCostings.length > 0) {
          const costing = task.taskCostings[0];
        }
        
        const actualTotalValue = calculateActualTotalValue(task, completedValues.accomplishment, { enableDebug: true });
        
        await taskService.completeTask(task.id, {
          completionAccomplishment: completedValues.accomplishment,
          completionComments: completionComments,
          completionDate: formattedCompletionDate,
          userId: currentUser?.id,
          stage: TaskStage.INTERNAL_REVIEW,
          taskLocations: locationData,
          actualTotalValue: actualTotalValue
        });
        onComplete();
        onClose();
      } catch (error) {
        console.error('Error completing task:', error);
        setSubmitError(describeActionError(error, "Couldn't report the accomplishment. Try again."));
      }
    }
  };

  if (!visible) return null;

  return (
    <ResponsiveModal
      isOpen={visible}
      onClose={onClose}
      title="Report Accomplishment"
      footer={
        <>
          <Button
            variant="secondary"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!!validationError || ((getEffortCount(task) === 0) && !completionComments.trim())}
            onClick={handleSubmit}
          >
            Report Accomplishment
          </Button>
        </>
      }
    >
      <form onSubmit={(e) => { e.preventDefault(); }}>
        {submitError && (
          <Alert variant="danger" role="alert">
            {submitError}
          </Alert>
        )}
        {/* Completion Date at the top */}
        <Row className="tw-mb-4">
          <Col xs={12}>
            <Form.Label>Completion Date</Form.Label>
            <Form.Control
              type="date"
              name="completionDate"
              value={completionDate}
              onChange={handleChange}
              required
            />
            <small className="tw-text-content-muted">{getDateSourceDescription()}</small>
          </Col>
        </Row>

        {/* Main content in two columns */}
        <Row className="tw-mb-4">
          {/* Left Column - Planned Values */}
          <Col md={6}>
            <h5 className="tw-mb-3">Planned Values</h5>

            {/* Planned Accomplishment */}
            <div className="tw-mb-3">
              <Form.Label>Planned Accomplishment</Form.Label>
              <InputGroup>
                <Form.Control
                  type="text"
                  value={plannedValues.accomplishment}
                  disabled
                />
                <InputGroup.Text>{plannedValues.unitOfMeasurement}</InputGroup.Text>
              </InputGroup>
            </div>

            {/* Planned GPS Locations */}
            <div className="tw-mb-3">
              <Form.Label>Planned Locations</Form.Label>
              <Row>
                <Col md={6}>
                  <Form.Label className="tw-text-sm">Start Location</Form.Label>
                  <InputGroup className="tw-mb-2">
                    <InputGroup.Text>Lat</InputGroup.Text>
                    <Form.Control
                      type="text"
                      value={plannedValues.startLatitude}
                      disabled
                    />
                  </InputGroup>
                  <InputGroup>
                    <InputGroup.Text>Lng</InputGroup.Text>
                    <Form.Control
                      type="text"
                      value={plannedValues.startLongitude}
                      disabled
                    />
                  </InputGroup>
                </Col>
                <Col md={6}>
                  <Form.Label className="tw-text-sm">End Location</Form.Label>
                  <InputGroup className="tw-mb-2">
                    <InputGroup.Text>Lat</InputGroup.Text>
                    <Form.Control
                      type="text"
                      value={plannedValues.endLatitude}
                      disabled
                    />
                  </InputGroup>
                  <InputGroup>
                    <InputGroup.Text>Lng</InputGroup.Text>
                    <Form.Control
                      type="text"
                      value={plannedValues.endLongitude}
                      disabled
                    />
                  </InputGroup>
                </Col>
              </Row>
            </div>

            {/* Expected Total Value */}
            <div className="tw-mb-3">
              <Form.Label>Expected Total Value</Form.Label>
              <InputGroup>
                <InputGroup.Text>$</InputGroup.Text>
                <Form.Control
                  type="text"
                  value={calculateExpectedTotalValueWithLogic(task, { enableDebug: true }).toFixed(2)}
                  disabled
                />
              </InputGroup>
              {/* Show calculation details for Unit Rate tasks */}
              {task.taskCostings?.[0]?.taskType === 'Unit Rate' && (
                <small className="tw-text-content-muted">
                  {(() => {
                    const baseRate = task.taskCostings[0].baseRate || 0;
                    const rateMultiplier = task.taskCostings[0].rateMultiplier || 1;
                    const rateAdjustment = task.taskCostings[0].rateAdjustment || 0;
                    const effectiveRate = rateAdjustment > 0 ? rateAdjustment : baseRate;
                    const adjustedRate = effectiveRate * rateMultiplier;

                    if (rateAdjustment > 0) {
                      return `Rate: ${rateAdjustment} (adjusted) × ${rateMultiplier} = ${adjustedRate.toFixed(2)} per unit`;
                    } else {
                      return `Rate: ${baseRate} × ${rateMultiplier} = ${adjustedRate.toFixed(2)} per unit`;
                    }
                  })()}
                </small>
              )}
            </div>
          </Col>

          {/* Right Column - Actual Values */}
          <Col md={6}>
            <h5 className="tw-mb-3">Actual Values</h5>

            {/* Actual Accomplishment */}
            <div className="tw-mb-3">
              <Form.Label>Actual Accomplishment</Form.Label>
              <InputGroup>
                <Form.Control
                  type="number"
                  name="accomplishment"
                  value={completedValues.accomplishment}
                  onChange={handleChange}
                  required
                  min={hasPermission('quantified:zero_accomplishment') ? "0" : "0.01"}
                  step="any"
                />
                <InputGroup.Text>{completedValues.unitOfMeasurement}</InputGroup.Text>
              </InputGroup>
              {!hasPermission('quantified:zero_accomplishment') && (
                <small className="tw-text-content-muted">
                  Minimum value: 0.01 (contact administrator for 0 accomplishment permission)
                </small>
              )}
            </div>

            {/* Road Distance Information - only show for road assets (linear assets) */}
            {(() => {
              // Hide Road Distance Information section when task is Closed or Finalized
              const isTaskClosedOrFinalized = task?.status === TaskStage.CLOSED || task?.status === TaskStage.FINALIZED;
              if (isTaskClosedOrFinalized) {
                return null;
              }

              // Check if this is a road asset with both start and end coordinates
              const hasStartCoords = completedValues.startLatitude && completedValues.startLongitude;
              const hasEndCoords = completedValues.endLatitude && completedValues.endLongitude;
              const hasValidCoordinates = hasStartCoords && hasEndCoords;
              
              // Get the asset from the task to check if it's a road asset
              const taskAsset = task.taskLocations?.[0]?.asset;
              const isRoadAsset = taskAsset?.assetType?.toLowerCase() === 'road';
              
              return hasValidCoordinates && isRoadAsset && (
                <DistanceDisplay
                  startLat={completedValues.startLatitude ? parseFloat(String(completedValues.startLatitude)) : null}
                  startLng={completedValues.startLongitude ? parseFloat(String(completedValues.startLongitude)) : null}
                  endLat={completedValues.endLatitude ? parseFloat(String(completedValues.endLatitude)) : null}
                  endLng={completedValues.endLongitude ? parseFloat(String(completedValues.endLongitude)) : null}
                  onApplyDistance={(distance: number) => {
                    setCompletedValues(prev => ({
                      ...prev,
                      accomplishment: distance
                    }));
                  }}
                  unitOfMeasure={completedValues.unitOfMeasurement}
                  disabled={false}
                  assetLength={taskAsset?.length}
                />
              );
            })()}

            {/* Actual GPS Locations */}
            <div className="tw-mb-3">
              <Form.Label>Actual Locations</Form.Label>
              <Row>
                <Col md={6}>
                  <Form.Label className="tw-text-sm">Start Location</Form.Label>
                  <InputGroup className="tw-mb-2">
                    <InputGroup.Text>Lat</InputGroup.Text>
                    <Form.Control
                      type="text"
                      value={completedValues.startLatitude}
                      readOnly
                    />
                  </InputGroup>
                  <InputGroup>
                    <InputGroup.Text>Lng</InputGroup.Text>
                    <Form.Control
                      type="text"
                      value={completedValues.startLongitude}
                      readOnly
                    />
                  </InputGroup>
                </Col>
                <Col md={6}>
                  <Form.Label className="tw-text-sm">End Location</Form.Label>
                  <InputGroup className="tw-mb-2">
                    <InputGroup.Text>Lat</InputGroup.Text>
                    <Form.Control
                      type="text"
                      value={completedValues.endLatitude}
                      readOnly
                    />
                  </InputGroup>
                  <InputGroup>
                    <InputGroup.Text>Lng</InputGroup.Text>
                    <Form.Control
                      type="text"
                      value={completedValues.endLongitude}
                      readOnly
                    />
                  </InputGroup>
                </Col>
              </Row>
            </div>

            {/* Show/Hide Map Button */}
            <div className="tw-mb-3">
              <Button
                variant={showMap ? 'primary' : 'outline-primary'}
                onClick={(e) => {
                  e.preventDefault();
                  setShowMap(!showMap);
                }}
              >
                {showMap ? 'Hide Map' : 'Show Map'}
              </Button>
            </div>

            {/* Google Maps - show when map button is toggled */}
            {showMap && (
              <>
                <div className="tw-mb-3">
                  <Form.Label>Select Location to Update:</Form.Label>
                  <ButtonGroup className="tw-w-full">
                    <Button
                      variant={activeLocationType === 'start' ? 'primary' : 'outline-primary'}
                      onClick={() => setActiveLocationType('start')}
                    >
                      Start Location
                    </Button>
                    <Button
                      variant={activeLocationType === 'end' ? 'primary' : 'outline-primary'}
                      onClick={() => setActiveLocationType('end')}
                    >
                      End Location
                    </Button>
                  </ButtonGroup>
                </div>
                <GoogleGPSMap
                  startLocation={{
                    lat: completedValues.startLatitude,
                    lng: completedValues.startLongitude
                  }}
                  endLocation={{
                    lat: completedValues.endLatitude,
                    lng: completedValues.endLongitude
                  }}
                  onLocationSelect={handleLocationSelect}
                  locationType={activeLocationType}
                  autoFitBounds={false}
                />
              </>
            )}

            {/* Actual Total Value */}
            <div className="tw-mb-3">
              <Form.Label>Actual Total Value</Form.Label>
              <InputGroup>
                <InputGroup.Text>$</InputGroup.Text>
                <Form.Control
                  type="text"
                  value={calculateActualTotalValue(task, completedValues.accomplishment, { enableDebug: true }).toFixed(2)}
                  readOnly
                />
              </InputGroup>
              {/* Show calculation details for Unit Rate tasks */}
              {task.taskCostings?.[0]?.taskType === 'Unit Rate' && (
                <small className="tw-text-content-muted">
                  {(() => {
                    const baseRate = task.taskCostings[0].baseRate || 0;
                    const rateMultiplier = task.taskCostings[0].rateMultiplier || 1;
                    const rateAdjustment = task.taskCostings[0].rateAdjustment || 0;
                    const effectiveRate = rateAdjustment > 0 ? rateAdjustment : baseRate;
                    const adjustedRate = effectiveRate * rateMultiplier;

                    if (rateAdjustment > 0) {
                      return `Rate: ${rateAdjustment} (adjusted) × ${rateMultiplier} = ${adjustedRate.toFixed(2)} per unit × ${completedValues.accomplishment} units`;
                    } else {
                      return `Rate: ${baseRate} × ${rateMultiplier} = ${adjustedRate.toFixed(2)} per unit × ${completedValues.accomplishment} units`;
                    }
                  })()}
                </small>
              )}
            </div>

            {validationError && (
              <Alert variant="warning">
                <i className="bi bi-exclamation-triangle tw-mr-2"></i>
                {validationError}
              </Alert>
            )}
          </Col>
        </Row>

        {/* GPS Changed Checkbox and Comments Section */}
        <Row>
          <Col xs={12}>
            {/* GPS Changed Checkbox */}
            {quantityChanged && (
              <div className="tw-mb-3">
                <Form.Check
                  type="checkbox"
                  id="gpsChanged"
                  name="gpsChanged"
                  checked={gpsChanged}
                  onChange={handleChange}
                  label="Did the GPS location change with the change in quantity?"
                />
              </div>
            )}

            {/* Comments Section */}
            {(getEffortCount(task) === 0) && (
              <Alert variant="warning" className="tw-mb-3">
                <i className="bi bi-exclamation-triangle tw-mr-2"></i>
                <strong>Warning:</strong> This task has no efforts recorded. Comments are required to explain why no efforts were recorded before completing the task.
              </Alert>
            )}

            <Form.Label>
              Completion Comments
              {(getEffortCount(task) === 0) && <span className="tw-text-danger tw-ml-1">*</span>}
            </Form.Label>
            <Form.Control
              id={COMMENTS_FIELD_ID}
              as="textarea"
              name="completionComments"
              value={completionComments}
              onChange={handleChange}
              rows={4}
              required={(getEffortCount(task) === 0)}
              isInvalid={!!commentsError}
              aria-invalid={!!commentsError}
              aria-describedby={commentsError ? `${COMMENTS_FIELD_ID}-error` : undefined}
              placeholder={(getEffortCount(task) === 0) ? "Comments are required when no efforts are recorded. Please explain why..." : "Please provide your comments..."}
            />
            {commentsError && (
              <Form.Control.Feedback type="invalid" id={`${COMMENTS_FIELD_ID}-error`}>
                {commentsError}
              </Form.Control.Feedback>
            )}
            {(getEffortCount(task) === 0) && (
              <div className="tw-mt-1 tw-text-sm tw-text-content-muted">
                <small>Comments are mandatory when completing a task without any efforts.</small>
              </div>
            )}
          </Col>
        </Row>
      </form>
    </ResponsiveModal>
  );
};

export default CompletionModal; 
